import React, { useEffect, useState, useRef, createElement } from 'react';
import { PageHero } from '../components/layout/PageHero';
import { FormStepper } from '../components/supplier/FormStepper';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { UploadIcon, CheckCircleIcon, FileTextIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { isValidPhoneNumber } from 'libphonenumber-js';
declare global {
  interface Window {
    Forminit: any;
  }
}
const FORMINIT_FORM_ID = 'jrj3prirapz';
const MAX_TOTAL_FILE_SIZE_MB = 25;
type DocKey = 'incorporation' | 'businessRegistration' | 'companyProfile' | 'proofOfAddress' | 'taxRegistration' | 'bankConfirmation';
interface DocumentDef {
  key: DocKey;
  label: string;
  required: boolean;
  fieldName: string;
}
const documentDefs: DocumentDef[] = [{
  key: 'incorporation',
  label: 'Certificate of Incorporation',
  required: true,
  fieldName: 'fi-file-incorporation'
}, {
  key: 'businessRegistration',
  label: 'Business Registration Certificate',
  required: true,
  fieldName: 'fi-file-businessRegistration'
}, {
  key: 'companyProfile',
  label: 'Company Profile',
  required: false,
  fieldName: 'fi-file-companyProfile'
}, {
  key: 'proofOfAddress',
  label: 'Proof of Registered Address',
  required: false,
  fieldName: 'fi-file-proofOfAddress'
}, {
  key: 'taxRegistration',
  label: 'Tax Registration Certificate',
  required: false,
  fieldName: 'fi-file-taxRegistration'
}, {
  key: 'bankConfirmation',
  label: 'Bank Confirmation Letter',
  required: false,
  fieldName: 'fi-file-bankConfirmation'
}];
export function SupplierRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [forminitReady, setForminitReady] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const steps = ['General Information', 'Business Information', 'Financial & Banking', 'Declaration & Submit'];
  const [formData, setFormData] = useState({
    // Step 1
    companyName: '',
    parentCompany: '',
    address: '',
    phone: '',
    whatsappNumber: '',
    email: '',
    contactFullName: '',
    contactTitle: '',
    typeOfOrganisation: '',
    yearEstablished: '',
    registrationNumber: '',
    // Step 2
    categories: [] as string[],
    otherCategory: '',
    areaOfSpecialization: '',
    employeesTotal: '',
    employeesFullTime: '',
    employeesPartTime: '',
    authRepName: '',
    authRepTitle: '',
    authRepPhone: '',
    authRepEmail: '',
    // Step 3
    accountName: '',
    bankName: '',
    bankAddress: '',
    accountNumber: '',
    iban: '',
    swiftBic: '',
    currency: '',
    auditedFinancials: '',
    turnoverCurrent: '',
    turnoverPrevious: '',
    // Step 4
    confirmAccurate: false,
    agreeCodeOfConduct: false,
    understandFalseInfo: false,
    declarationName: '',
    declarationTitle: '',
    declarationDate: '',
    signature: ''
  });
  const [files, setFiles] = useState<Record<DocKey, File | null>>({
    incorporation: null,
    businessRegistration: null,
    companyProfile: null,
    proofOfAddress: null,
    taxRegistration: null,
    bankConfirmation: null
  });
  const fileInputRefs = {
    incorporation: useRef<HTMLInputElement>(null),
    businessRegistration: useRef<HTMLInputElement>(null),
    companyProfile: useRef<HTMLInputElement>(null),
    proofOfAddress: useRef<HTMLInputElement>(null),
    taxRegistration: useRef<HTMLInputElement>(null),
    bankConfirmation: useRef<HTMLInputElement>(null)
  };
  // Load Forminit SDK
  useEffect(() => {
    if (document.getElementById('forminit-sdk')) {
      setForminitReady(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'forminit-sdk';
    script.src = 'https://forminit.com/sdk/v1/forminit.js';
    script.async = true;
    script.onload = () => setForminitReady(true);
    document.body.appendChild(script);
  }, []);
  const categoriesList = ['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent'];
  // Phone number must be E.164 format AND a real, dialable number per its country.
  const normalizePhone = (raw: string): string => {
    let p = (raw || '').trim().replace(/[\s\-().]/g, '');
    if (p.startsWith('00')) p = '+' + p.slice(2);
    if (!p.startsWith('+') && /^\d{8,}$/.test(p)) p = '+' + p;
    return p;
  };
  const isValidE164 = (phone: string): boolean => {
    if (!/^\+\d{8,15}$/.test(phone)) return false;
    try {
      return isValidPhoneNumber(phone);
    } catch {
      return false;
    }
  };
  const handlePhoneBlur = () => {
    if (!formData.phone) {
      setPhoneError(null);
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (normalized !== formData.phone) {
      setFormData(prev => ({
        ...prev,
        phone: normalized
      }));
    }
    if (!isValidE164(normalized)) {
      setPhoneError('This does not look like a valid phone number. Please double-check the country code and digits (e.g. +447700900123 for UK, +15550101234 for US, +306900000000 for Greece).');
    } else {
      setPhoneError(null);
    }
  };
  const handleNext = () => {
    if (currentStep === 1) {
      const normalized = normalizePhone(formData.phone);
      if (normalized !== formData.phone) {
        setFormData(prev => ({
          ...prev,
          phone: normalized
        }));
      }
      if (!isValidE164(normalized)) {
        setPhoneError('Please enter a valid, real phone number in international format starting with + and the correct country code (e.g. +447700900123).');
        return;
      }
      setPhoneError(null);
      if (!formData.whatsappNumber.trim()) {
        setWhatsappError('Please provide a WhatsApp number. If you don’t use WhatsApp, enter your telephone number here instead.');
        return;
      }
      setWhatsappError(null);
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setSubmitError(null);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setSubmitError(null);
    }
  };
  const handleFileChange = (key: DocKey, file: File | null) => {
    setFiles(prev => ({
      ...prev,
      [key]: file
    }));
  };
  const totalFileSizeMB = (): number => {
    return Object.values(files).reduce((sum, f) => sum + (f ? f.size : 0), 0) / (1024 * 1024);
  };
  const handleSubmit = async () => {
    setSubmitError(null);
    // Validation: required declarations + signature
    if (!formData.confirmAccurate || !formData.agreeCodeOfConduct || !formData.understandFalseInfo) {
      setSubmitError('Please tick all three declaration checkboxes to proceed.');
      return;
    }
    if (!formData.declarationName.trim() || !formData.signature.trim()) {
      setSubmitError('Please provide your name and digital signature in the declaration.');
      return;
    }
    // Required docs check
    const missingDocs = documentDefs.filter(d => d.required && !files[d.key]);
    if (missingDocs.length > 0) {
      setSubmitError(`Missing required documents: ${missingDocs.map(d => d.label).join(', ')}. Please return to the Financial & Banking step.`);
      return;
    }
    // Size check
    if (totalFileSizeMB() > MAX_TOTAL_FILE_SIZE_MB) {
      setSubmitError(`Total file size exceeds ${MAX_TOTAL_FILE_SIZE_MB}MB. Please reduce file sizes and try again.`);
      return;
    }
    if (!forminitReady || !window.Forminit) {
      setSubmitError('Submission service is still loading. Please wait a moment and try again.');
      return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      // Phone — re-normalize defensively right before send
      const normalizedPhone = normalizePhone(formData.phone);
      if (!isValidE164(normalizedPhone)) {
        setSubmitError('The phone number you entered does not appear to be valid for the country code. Please go back to Step 1 and correct it (e.g. +447700900123).');
        setIsSubmitting(false);
        return;
      }
      // Standard Forminit sender fields
      fd.append('fi-sender-fullName', formData.contactFullName);
      fd.append('fi-sender-email', formData.email);
      fd.append('fi-sender-phone', normalizedPhone);
      // Step 1
      fd.append('fi-text-companyName', formData.companyName);
      fd.append('fi-text-parentCompany', formData.parentCompany);
      fd.append('fi-text-fullAddress', formData.address);
      fd.append('fi-text-whatsappNumber', formData.whatsappNumber);
      fd.append('fi-text-contactTitle', formData.contactTitle);
      fd.append('fi-text-typeOfOrganisation', formData.typeOfOrganisation);
      fd.append('fi-text-yearEstablished', formData.yearEstablished);
      fd.append('fi-text-registrationNumber', formData.registrationNumber);
      // Step 2
      const categoriesPayload = formData.categories.map(c => c === 'Other' && formData.otherCategory.trim() ? `Other: ${formData.otherCategory.trim()}` : c).join(', ');
      fd.append('fi-text-businessCategories', categoriesPayload);
      fd.append('fi-text-areaOfSpecialization', formData.areaOfSpecialization);
      fd.append('fi-text-employeesTotal', formData.employeesTotal);
      fd.append('fi-text-employeesFullTime', formData.employeesFullTime);
      fd.append('fi-text-employeesPartTime', formData.employeesPartTime);
      fd.append('fi-text-authRepName', formData.authRepName);
      fd.append('fi-text-authRepTitle', formData.authRepTitle);
      fd.append('fi-text-authRepPhone', formData.authRepPhone);
      fd.append('fi-text-authRepEmail', formData.authRepEmail);
      // Step 3
      fd.append('fi-text-accountName', formData.accountName);
      fd.append('fi-text-bankName', formData.bankName);
      fd.append('fi-text-bankAddress', formData.bankAddress);
      fd.append('fi-text-accountNumber', formData.accountNumber);
      fd.append('fi-text-iban', formData.iban);
      fd.append('fi-text-swiftBic', formData.swiftBic);
      fd.append('fi-text-currency', formData.currency);
      fd.append('fi-text-auditedFinancials', formData.auditedFinancials);
      fd.append('fi-text-turnoverCurrent', formData.turnoverCurrent);
      fd.append('fi-text-turnoverPrevious', formData.turnoverPrevious);
      // Step 4
      fd.append('fi-text-declarations', [formData.confirmAccurate ? '✓ Confirmed information is accurate' : '', formData.agreeCodeOfConduct ? '✓ Agreed to Supplier Code of Conduct' : '', formData.understandFalseInfo ? '✓ Understood false information consequences' : ''].filter(Boolean).join(' | '));
      fd.append('fi-text-declarationName', formData.declarationName);
      fd.append('fi-text-declarationTitle', formData.declarationTitle);
      fd.append('fi-text-declarationDate', formData.declarationDate);
      fd.append('fi-text-signature', formData.signature);
      // Files
      documentDefs.forEach(d => {
        const f = files[d.key];
        if (f) fd.append(d.fieldName, f);
      });
      const forminit = new window.Forminit();
      const result = await forminit.submit(FORMINIT_FORM_ID, fd);
      if (result && result.error) {
        setSubmitError(result.error.message || 'Submission failed. Please try again.');
        setIsSubmitting(false);
        return;
      }
      setSubmitted(true);
      setIsSubmitting(false);
    } catch (err: any) {
      setSubmitError(err?.message || 'There was a problem submitting your registration. Please try again.');
      setIsSubmitting(false);
    }
  };
  if (submitted) {
    return <div data-id="element-834">
        <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Procurement'
      }, {
        label: 'Expression of Interest'
      }]} data-id="element-835" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-id="element-836">
          <Card data-id="element-837">
            <div className="w-20 h-20 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-838">
              <CheckCircleIcon size={48} className="text-au-green" data-id="element-839" />
            </div>
            <h2 className="text-3xl font-serif text-charcoal mb-3" data-id="element-840">
              Expression of Interest Submitted
            </h2>
            <div className="w-16 h-1 bg-au-gold mx-auto mb-6" data-id="element-841"></div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-842">
              Thank you for submitting your Expression of Interest to the
              African Union Commission. Your submission and supporting documents
              have been received. Our procurement team will review your
              Expression of Interest and contact you within{' '}
              <strong data-id="element-843">5–10 business days</strong>.
            </p>
            <p className="text-sm text-gray-600 mb-8" data-id="element-844">
              For urgent enquiries, contact{' '}
              <a href="mailto:procurement@aucommission.org" className="text-au-green font-medium hover:underline" data-id="element-845">
                procurement@aucommission.org
              </a>
            </p>
            <Button variant="secondary" onClick={() => {
            window.location.href = '/';
          }} data-id="element-846">
              Return to Home
            </Button>
          </Card>
        </div>
      </div>;
  }
  return <div data-id="element-847">
      <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Expression of Interest'
    }]} data-id="element-848" />

      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-849">
        <div className="mb-8 bg-au-green-light/50 border border-au-green/20 rounded-lg p-6" data-id="element-850">
          <h2 className="text-xl font-serif text-charcoal mb-3" data-id="element-851">
            Expression of Interest (EOI) Form
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed" data-id="element-852">
            This Expression of Interest (EOI) form forms part of the supplier
            engagement process associated with the contract award process and
            must be completed in full by the interested party. Information
            provided in this form shall be used solely for official supplier
            engagement, administration and contract award purposes and shall be
            treated in confidence.
          </p>
        </div>

        <FormStepper currentStep={currentStep} steps={steps} data-id="element-853" />

        <Card data-id="element-854">
          {currentStep === 1 && <div data-id="element-855">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-856">
                SECTION 1: GENERAL INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-857"></div>

              <div className="space-y-5" data-id="element-858">
                <div data-id="element-859">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-860">
                    1. Name of Company <span className="text-au-red" data-id="element-861">*</span>
                  </label>
                  <input type="text" value={formData.companyName} onChange={e => setFormData({
                ...formData,
                companyName: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-862" />
                </div>

                <div data-id="element-863">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-864">
                    1.1 Parent Company (if applicable)
                  </label>
                  <input type="text" value={formData.parentCompany} onChange={e => setFormData({
                ...formData,
                parentCompany: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-865" />
                </div>

                <div data-id="element-866">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-867">
                    2. Full Address of the Company{' '}
                    <span className="text-au-red" data-id="element-868">*</span>
                  </label>
                  <textarea rows={3} value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-869" />
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-870">
                  <div data-id="element-871">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-872">
                      3. Telephone Number (including country code){' '}
                      <span className="text-au-red" data-id="element-873">*</span>
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} onBlur={handlePhoneBlur} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green ${phoneError ? 'border-au-red' : 'border-gray-border'}`} placeholder="+447700900123" data-id="element-874" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-875">
                      Use international format starting with + and country code.
                    </p>
                    {phoneError && <p className="mt-1 text-xs text-au-red" data-id="element-876">{phoneError}</p>}
                  </div>
                  <div data-id="element-877">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-878">
                      WhatsApp Number <span className="text-au-red" data-id="element-879">*</span>
                    </label>
                    <input type="tel" value={formData.whatsappNumber} onChange={e => setFormData({
                  ...formData,
                  whatsappNumber: e.target.value
                })} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green ${whatsappError ? 'border-au-red' : 'border-gray-border'}`} placeholder="+447700900123" data-id="element-880" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-881">
                      Used for quicker contact. If you don’t use WhatsApp, enter
                      your telephone number here instead.
                    </p>
                    {whatsappError && <p className="mt-1 text-xs text-au-red" data-id="element-882">
                        {whatsappError}
                      </p>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-883">
                  <div data-id="element-884">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-885">
                      4. Email Address <span className="text-au-red" data-id="element-886">*</span>
                    </label>
                    <input type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-887" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-888">
                  <div data-id="element-889">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-890">
                      5. Name of Contact Person{' '}
                      <span className="text-au-red" data-id="element-891">*</span>
                    </label>
                    <input type="text" value={formData.contactFullName} onChange={e => setFormData({
                  ...formData,
                  contactFullName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-892" />
                  </div>
                  <div data-id="element-893">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-894">
                      Title of Contact Person{' '}
                      <span className="text-au-red" data-id="element-895">*</span>
                    </label>
                    <input type="text" value={formData.contactTitle} onChange={e => setFormData({
                  ...formData,
                  contactTitle: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-896" />
                  </div>
                </div>

                <div data-id="element-897">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-898">
                    6. Type of Organisation{' '}
                    <span className="text-au-red" data-id="element-899">*</span>
                  </label>
                  <select value={formData.typeOfOrganisation} onChange={e => setFormData({
                ...formData,
                typeOfOrganisation: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green bg-white" data-id="element-900">
                    <option value="" data-id="element-901">Select type</option>
                    <option value="Private Company" data-id="element-902">Private Company</option>
                    <option value="State Enterprise" data-id="element-903">State Enterprise</option>
                    <option value="Other" data-id="element-904">Other</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-905">
                  <div data-id="element-906">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-907">
                      7. Year Established <span className="text-au-red" data-id="element-908">*</span>
                    </label>
                    <input type="text" value={formData.yearEstablished} onChange={e => setFormData({
                  ...formData,
                  yearEstablished: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-909" />
                  </div>
                  <div data-id="element-910">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-911">
                      8. Company Registration Number{' '}
                      <span className="text-au-red" data-id="element-912">*</span>
                    </label>
                    <input type="text" value={formData.registrationNumber} onChange={e => setFormData({
                  ...formData,
                  registrationNumber: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-913" />
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 2 && <div data-id="element-914">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-915">
                SECTION 2: BUSINESS INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-916"></div>

              <div className="space-y-6" data-id="element-917">
                <div data-id="element-918">
                  <label className="block text-sm font-medium text-charcoal mb-3" data-id="element-919">
                    9. Business Category (Tick as applicable)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3" data-id="element-920">
                    {categoriesList.map(cat => <label key={cat} className="flex items-center gap-2 p-3 border border-gray-border rounded-lg hover:bg-au-green-light cursor-pointer transition-colors" data-id="element-921">
                        <input type="checkbox" checked={formData.categories.includes(cat)} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        categories: [...formData.categories, cat]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        categories: formData.categories.filter(c => c !== cat)
                      });
                    }
                  }} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-922" />
                        <span className="text-sm" data-id="element-923">{cat}</span>
                      </label>)}
                    <label className="flex items-center gap-2 p-3 border border-gray-border rounded-lg hover:bg-au-green-light cursor-pointer transition-colors" data-id="element-924">
                      <input type="checkbox" checked={formData.categories.includes('Other')} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        categories: [...formData.categories, 'Other']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        categories: formData.categories.filter(c => c !== 'Other'),
                        otherCategory: ''
                      });
                    }
                  }} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-925" />
                      <span className="text-sm" data-id="element-926">Other</span>
                    </label>
                  </div>
                  {formData.categories.includes('Other') && <div className="mt-3" data-id="element-927">
                      <input type="text" value={formData.otherCategory} onChange={e => setFormData({
                  ...formData,
                  otherCategory: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Please specify" data-id="element-928" />
                    </div>}
                </div>

                <div data-id="element-929">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-930">
                    10. Area of Specialization{' '}
                    <span className="text-au-red" data-id="element-931">*</span>
                  </label>
                  <textarea rows={3} value={formData.areaOfSpecialization} onChange={e => setFormData({
                ...formData,
                areaOfSpecialization: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-932" />
                </div>

                <div data-id="element-933">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-934">
                    11. Number of Employees
                  </label>
                  <div className="grid grid-cols-3 gap-4" data-id="element-935">
                    <div data-id="element-936">
                      <input type="number" value={formData.employeesTotal} onChange={e => setFormData({
                    ...formData,
                    employeesTotal: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Total" data-id="element-937" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-938">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-939">
                        Full Time:
                      </span>
                      <input type="number" value={formData.employeesFullTime} onChange={e => setFormData({
                    ...formData,
                    employeesFullTime: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-940" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-941">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-942">
                        Part Time:
                      </span>
                      <input type="number" value={formData.employeesPartTime} onChange={e => setFormData({
                    ...formData,
                    employeesPartTime: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-943" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-border pt-6" data-id="element-944">
                  <label className="block text-sm font-medium text-charcoal mb-4" data-id="element-945">
                    12. Authorised Representative
                  </label>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-946">
                    <div data-id="element-947">
                      <input type="text" value={formData.authRepName} onChange={e => setFormData({
                    ...formData,
                    authRepName: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Name" data-id="element-948" />
                    </div>
                    <div data-id="element-949">
                      <input type="text" value={formData.authRepTitle} onChange={e => setFormData({
                    ...formData,
                    authRepTitle: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Title" data-id="element-950" />
                    </div>
                    <div data-id="element-951">
                      <input type="tel" value={formData.authRepPhone} onChange={e => setFormData({
                    ...formData,
                    authRepPhone: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Telephone" data-id="element-952" />
                    </div>
                    <div data-id="element-953">
                      <input type="email" value={formData.authRepEmail} onChange={e => setFormData({
                    ...formData,
                    authRepEmail: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Email" data-id="element-954" />
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 3 && <div data-id="element-955">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-956">
                SECTION 3: FINANCIAL & BANKING INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-957"></div>

              <div className="space-y-5 mb-8" data-id="element-958">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-959">
                  <div data-id="element-960">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-961">
                      Account Name
                    </label>
                    <input type="text" value={formData.accountName} onChange={e => setFormData({
                  ...formData,
                  accountName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-962" />
                  </div>
                  <div data-id="element-963">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-964">
                      Bank Name
                    </label>
                    <input type="text" value={formData.bankName} onChange={e => setFormData({
                  ...formData,
                  bankName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-965" />
                  </div>
                </div>

                <div data-id="element-966">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-967">
                    Bank Address
                  </label>
                  <input type="text" value={formData.bankAddress} onChange={e => setFormData({
                ...formData,
                bankAddress: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-968" />
                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="element-969">
                  <div data-id="element-970">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-971">
                      Account Number
                    </label>
                    <input type="text" value={formData.accountNumber} onChange={e => setFormData({
                  ...formData,
                  accountNumber: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-972" />
                  </div>
                  <div data-id="element-973">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-974">
                      IBAN
                    </label>
                    <input type="text" value={formData.iban} onChange={e => setFormData({
                  ...formData,
                  iban: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-975" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="element-976">
                  <div data-id="element-977">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-978">
                      SWIFT/BIC
                    </label>
                    <input type="text" value={formData.swiftBic} onChange={e => setFormData({
                  ...formData,
                  swiftBic: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-979" />
                  </div>
                  <div data-id="element-980">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-981">
                      Currency of Account
                    </label>
                    <input type="text" value={formData.currency} onChange={e => setFormData({
                  ...formData,
                  currency: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-982" />
                  </div>
                </div>

                <div className="border-t border-gray-border pt-5" data-id="element-983">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-984">
                    13. Audited Financial Statements Available
                  </label>
                  <div className="flex gap-4" data-id="element-985">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-986">
                      <input type="radio" name="auditedFinancials" value="Yes" checked={formData.auditedFinancials === 'Yes'} onChange={e => setFormData({
                    ...formData,
                    auditedFinancials: e.target.value
                  })} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-987" />
                      <span className="text-sm" data-id="element-988">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-989">
                      <input type="radio" name="auditedFinancials" value="No" checked={formData.auditedFinancials === 'No'} onChange={e => setFormData({
                    ...formData,
                    auditedFinancials: e.target.value
                  })} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-990" />
                      <span className="text-sm" data-id="element-991">No</span>
                    </label>
                  </div>
                </div>

                <div data-id="element-992">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-993">
                    14. Estimated Annual Turnover
                  </label>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-994">
                    <div className="flex items-center gap-2" data-id="element-995">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-996">
                        Current Year:
                      </span>
                      <input type="text" value={formData.turnoverCurrent} onChange={e => setFormData({
                    ...formData,
                    turnoverCurrent: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-997" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-998">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-999">
                        Previous Year:
                      </span>
                      <input type="text" value={formData.turnoverPrevious} onChange={e => setFormData({
                    ...formData,
                    turnoverPrevious: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1000" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-border pt-6" data-id="element-1001">
                <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-1002">
                  15. Registration Documents Submitted
                </h3>
                <p className="text-sm text-gray-600 mb-6" data-id="element-1003">
                  Maximum total file size: {MAX_TOTAL_FILE_SIZE_MB}MB. Accepted
                  formats: PDF, DOC, DOCX, JPG, PNG. Current total:{' '}
                  <span className="font-bold text-au-green" data-id="element-1004">
                    {totalFileSizeMB().toFixed(2)}MB
                  </span>
                </p>
                <div className="space-y-4" data-id="element-1005">
                  {documentDefs.map(doc => {
                const file = files[doc.key];
                return <div key={doc.key} className={`border-2 border-dashed rounded-lg p-6 transition-colors ${file ? 'border-au-green bg-au-green-light' : 'border-gray-border hover:border-au-green'}`} data-id="element-1006">
                        <div className="flex items-center justify-between mb-3" data-id="element-1007">
                          <label className="text-sm font-medium text-charcoal" data-id="element-1008">
                            {doc.label}{' '}
                            {doc.required && <span className="text-au-red" data-id="element-1009">*</span>}
                          </label>
                          {file && <span className="text-xs text-au-green font-medium" data-id="element-1010">
                              {(file.size / (1024 * 1024)).toFixed(2)}MB
                            </span>}
                        </div>

                        <input ref={fileInputRefs[doc.key]} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => {
                    const f = e.target.files?.[0] || null;
                    handleFileChange(doc.key, f);
                  }} className="hidden" data-id="element-1011" />

                        {!file ? <div className="flex items-center gap-4" data-id="element-1012">
                            <Button variant="secondary" onClick={() => fileInputRefs[doc.key].current?.click()} className="flex items-center gap-2" data-id="element-1013">
                              <UploadIcon size={16} data-id="element-1014" />
                              Choose File
                            </Button>
                            <span className="text-sm text-gray-500" data-id="element-1015">
                              No file chosen
                            </span>
                          </div> : <div className="flex items-center justify-between bg-white border border-gray-border rounded-lg p-3" data-id="element-1016">
                            <div className="flex items-center gap-3 min-w-0" data-id="element-1017">
                              <FileTextIcon size={20} className="text-au-green flex-shrink-0" data-id="element-1018" />
                              <span className="text-sm text-charcoal truncate" data-id="element-1019">
                                {file.name}
                              </span>
                            </div>
                            <button type="button" onClick={() => {
                      handleFileChange(doc.key, null);
                      if (fileInputRefs[doc.key].current) {
                        fileInputRefs[doc.key].current!.value = '';
                      }
                    }} className="text-au-red hover:text-red-700 flex-shrink-0 ml-3" aria-label="Remove file" data-id="element-1020">
                              <XCircleIcon size={20} data-id="element-1021" />
                            </button>
                          </div>}
                      </div>;
              })}
                </div>

                {totalFileSizeMB() > MAX_TOTAL_FILE_SIZE_MB && <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1022">
                    <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-1023" />
                    <span data-id="element-1024">
                      Total file size exceeds {MAX_TOTAL_FILE_SIZE_MB}MB. Please
                      reduce file sizes before submitting.
                    </span>
                  </div>}
              </div>
            </div>}

          {currentStep === 4 && <div data-id="element-1025">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-1026">
                DECLARATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-1027"></div>

              <div className="space-y-6" data-id="element-1028">
                <div className="bg-au-green-light/50 border border-au-green/20 rounded-lg p-6" data-id="element-1029">
                  <p className="text-gray-800 text-sm leading-relaxed mb-4" data-id="element-1030">
                    I hereby certify that the information provided in this
                    Expression of Interest (EOI) Form and all supporting
                    documentation is true, complete and accurate.
                  </p>
                  <p className="text-gray-800 text-sm leading-relaxed" data-id="element-1031">
                    I further acknowledge and understand that this Expression of
                    Interest does not constitute a binding contract, and that
                    any subsequent supplier approval and formal Contract
                    Agreement shall be subject to the African Union Commission’s
                    evaluation and procurement procedures.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-1032">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1033">
                    <input type="checkbox" checked={formData.confirmAccurate} onChange={e => setFormData({
                  ...formData,
                  confirmAccurate: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1034" />
                    <span className="text-sm text-gray-700" data-id="element-1035">
                      I confirm that all information provided is accurate and
                      complete
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1036">
                    <input type="checkbox" checked={formData.agreeCodeOfConduct} onChange={e => setFormData({
                  ...formData,
                  agreeCodeOfConduct: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1037" />
                    <span className="text-sm text-gray-700" data-id="element-1038">
                      I agree to the African Union Supplier Code of Conduct
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1039">
                    <input type="checkbox" checked={formData.understandFalseInfo} onChange={e => setFormData({
                  ...formData,
                  understandFalseInfo: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1040" />
                    <span className="text-sm text-gray-700" data-id="element-1041">
                      I understand that providing false information may result
                      in disqualification
                    </span>
                  </label>
                </div>

                <div className="border-t border-gray-border pt-6" data-id="element-1042">
                  <div className="grid md:grid-cols-2 gap-5 mb-5" data-id="element-1043">
                    <div data-id="element-1044">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1045">
                        Name <span className="text-au-red" data-id="element-1046">*</span>
                      </label>
                      <input type="text" value={formData.declarationName} onChange={e => setFormData({
                    ...formData,
                    declarationName: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1047" />
                    </div>
                    <div data-id="element-1048">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1049">
                        Title
                      </label>
                      <input type="text" value={formData.declarationTitle} onChange={e => setFormData({
                    ...formData,
                    declarationTitle: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1050" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5" data-id="element-1051">
                    <div data-id="element-1052">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1053">
                        Date
                      </label>
                      <input type="date" value={formData.declarationDate} onChange={e => setFormData({
                    ...formData,
                    declarationDate: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1054" />
                    </div>
                    <div data-id="element-1055">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1056">
                        Signature <span className="text-au-red" data-id="element-1057">*</span>
                      </label>
                      <input type="text" value={formData.signature} onChange={e => setFormData({
                    ...formData,
                    signature: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Type your full name as signature" data-id="element-1058" />
                    </div>
                  </div>
                </div>

                {submitError && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1059">
                    <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-1060" />
                    <span data-id="element-1061">{submitError}</span>
                  </div>}
              </div>
            </div>}

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-border" data-id="element-1062">
            <Button variant="secondary" onClick={handlePrevious} disabled={currentStep === 1 || isSubmitting} className="disabled:opacity-50" data-id="element-1063">
              Previous
            </Button>
            {currentStep < steps.length ? <Button variant="primary" onClick={handleNext} data-id="element-1064">
                Next Step
              </Button> : <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 disabled:opacity-60" data-id="element-1065">
                {isSubmitting ? <>Submitting...</> : <>
                    <CheckCircleIcon size={20} data-id="element-1066" />
                    Submit Registration
                  </>}
              </Button>}
          </div>
        </Card>
      </div>
    </div>;
}